import { UnknownAction } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";
import {
  HYDRATE_STORYBOARD_SETTINGS_DOMAINS,
  HYDRATE_STORYBOARD_SETTINGS_DRAFT,
  HYDRATE_STORYBOARD_SETTINGS_GATING_CATALOG,
  HYDRATE_STORYBOARD_SETTINGS_STORYBOARD,
  HYDRATE_STORYBOARD_SETTINGS_STORYBOARD_PAGES,
  HYDRATE_STORYBOARD_SETTINGS_USERS,
  RESET_STORYBOARD_SETTINGS,
  RESET_STORYBOARD_SETTINGS_DRAFT,
  SET_STORYBOARD_SETTINGS_ERROR,
  SET_STORYBOARD_SETTINGS_LOADING,
  UPDATE_STORYBOARD_SETTINGS_DRAFT_FIELD,
} from "../../actions/actionTypes";
import {
  DomainResponse,
  GatingCatalogRecord,
  StoryboardPageListItem,
  StoryboardUserSummary,
} from "src/types/experiences/storyboardSettings";
import { StoryboardResponse } from "src/types/experiences/storyboardSettings/storyboard";
import { StoryboardSettingsSubKey } from "../../actions/experiences/storyboardSettings";

interface OneItemState<T> {
  item: T | null;
  loading: boolean;
  error: unknown;
  fetched: boolean;
}

interface ManyItemsState<T> {
  items: T[];
  loading: boolean;
  error: unknown;
  fetched: boolean;
}

export interface StoryboardSettingsState {
  storyboard: OneItemState<StoryboardResponse>;
  draft: { item: StoryboardResponse | null; isDirty: boolean };
  domains: ManyItemsState<DomainResponse>;
  // Workspace-wide gating catalog (read-only). UI-only — never sent to save.
  gatingCatalog: ManyItemsState<GatingCatalogRecord>;
  storyboardPages: ManyItemsState<StoryboardPageListItem>;
  users: ManyItemsState<StoryboardUserSummary>;
}

const oneItemInitial = <T>(): OneItemState<T> => ({
  item: null,
  loading: false,
  error: null,
  fetched: false,
});

const manyItemsInitial = <T>(): ManyItemsState<T> => ({
  items: [],
  loading: false,
  error: null,
  fetched: false,
});

const initialState: StoryboardSettingsState = {
  storyboard: oneItemInitial<StoryboardResponse>(),
  draft: { item: null, isDirty: false },
  domains: manyItemsInitial<DomainResponse>(),
  gatingCatalog: manyItemsInitial<GatingCatalogRecord>(),
  storyboardPages: manyItemsInitial<StoryboardPageListItem>(),
  users: manyItemsInitial<StoryboardUserSummary>(),
};

export const storyboardSettingsReducer = (
  state: StoryboardSettingsState = initialState,
  action: UnknownAction,
): StoryboardSettingsState => {
  switch (action.type) {
    case HYDRATE_STORYBOARD_SETTINGS_STORYBOARD:
      return {
        ...state,
        storyboard: {
          item: action.payload as StoryboardResponse,
          loading: false,
          error: null,
          fetched: true,
        },
      };

    case HYDRATE_STORYBOARD_SETTINGS_DOMAINS:
      return {
        ...state,
        domains: {
          items: action.payload as DomainResponse[],
          loading: false,
          error: null,
          fetched: true,
        },
      };

    case HYDRATE_STORYBOARD_SETTINGS_GATING_CATALOG:
      return {
        ...state,
        gatingCatalog: {
          items: action.payload as GatingCatalogRecord[],
          loading: false,
          error: null,
          fetched: true,
        },
      };

    case HYDRATE_STORYBOARD_SETTINGS_STORYBOARD_PAGES:
      return {
        ...state,
        storyboardPages: {
          items: action.payload as StoryboardPageListItem[],
          loading: false,
          error: null,
          fetched: true,
        },
      };

    case HYDRATE_STORYBOARD_SETTINGS_USERS:
      return {
        ...state,
        users: {
          items: action.payload as StoryboardUserSummary[],
          loading: false,
          error: null,
          fetched: true,
        },
      };

    case SET_STORYBOARD_SETTINGS_LOADING: {
      const { key, loading } = action.payload as {
        key: StoryboardSettingsSubKey;
        loading: boolean;
      };
      return {
        ...state,
        [key]: { ...state[key], loading },
      };
    }

    case SET_STORYBOARD_SETTINGS_ERROR: {
      const { key, error } = action.payload as {
        key: StoryboardSettingsSubKey;
        error: unknown;
      };
      return {
        ...state,
        [key]: { ...state[key], error, loading: false },
      };
    }

    case HYDRATE_STORYBOARD_SETTINGS_DRAFT:
      return {
        ...state,
        draft: {
          item: action.payload as StoryboardResponse,
          isDirty: false,
        },
      };

    case UPDATE_STORYBOARD_SETTINGS_DRAFT_FIELD: {
      const { key, value, silent } = action.payload as {
        key: keyof StoryboardResponse;
        value: unknown;
        silent?: boolean;
      };
      if (!state.draft.item) return state;

      const nextItem = {
        ...state.draft.item,
        [key]: value,
      } as StoryboardResponse;

      if (silent && state.storyboard.item) {
        const nextSaved = {
          ...state.storyboard.item,
          [key]: value,
        } as StoryboardResponse;
        return {
          ...state,
          storyboard: { ...state.storyboard, item: nextSaved },
          draft: {
            item: nextItem,
            isDirty: !isEqual(nextItem, nextSaved),
          },
        };
      }

      const isDirty = !isEqual(nextItem, state.storyboard.item);

      return {
        ...state,
        draft: {
          item: nextItem,
          isDirty,
        },
      };
    }

    case RESET_STORYBOARD_SETTINGS_DRAFT:
      return {
        ...state,
        draft: {
          item: state.storyboard.item,
          isDirty: false,
        },
      };

    case RESET_STORYBOARD_SETTINGS:
      // Keep gatingCatalog across storyboard navigations — it's workspace-wide.
      return { ...initialState, gatingCatalog: state.gatingCatalog };

    default:
      return state;
  }
};
